import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import '../../css/growthWheel.scss'
import {
  useGetGrowthWheelByRoleIdQuery,
  useGetGrowthWheelWithEvaluationsByUserIdQuery,
  useGetGrowthWheelUsersByGrowthWheelReviewerIdQuery,
} from '../../redux/growth-wheel.endpoints'
import {
  setGrowthWheelData,
  setUserCompetencyAreasData,
  setLoadingGrowthWheel,
  setCurrentCompetencyArea,
  setCurrentCompetency,
  evaluateFocusCompetenciesLimit,
  setCurrentAreasOfFocus,
} from '../../redux/growthWheel.slice'
import { RootState } from '../../redux/store'
import { User } from '../../types/User'
import { AdminContributorList } from '../../components/GrowthWheelComponents/AdminContributorList'
import GrowthWheelDashboard from '../GrowthWheelDashboard/GrowthWheelDashboard'
import GrowthWheelCompetencyAreaView from '../GrowthWheelCompetencyAreaView/GrowthWheelCompetencyAreaView'
import GrowthWheelCompetencyAssessment from '../GrowthWheelCompetencyView/GrowthWheelCompetencyView'
import { useSelector } from 'react-redux'

const GrowthWheelAdmin = ({ user }: { user: User }) => {
  const dispatch = useDispatch()
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)

  const { data: contributorsData } =
    useGetGrowthWheelUsersByGrowthWheelReviewerIdQuery({ reviewerId: user.id })

  const selectedUser = contributorsData?.find(c => c.id === selectedUserId)
  
  const roleId = selectedUser?.guildId ?? user.guildId

  const { data: growthWheelData, isLoading } = useGetGrowthWheelByRoleIdQuery(
    { roleId },
    { skip: !selectedUserId },
  )
  const { data: userCompetencyAreasData } =
    useGetGrowthWheelWithEvaluationsByUserIdQuery(
      { userId: selectedUserId! },
      { skip: !selectedUserId },
    )

  const currentCompetencyArea = useSelector(
    (state: RootState) => state.growthWheel.currentCompetencyArea,
  )
  const currentCompetency = useSelector(
    (state: RootState) => state.growthWheel.currentCompetency,
  )

  useEffect(() => {
    dispatch(setGrowthWheelData(growthWheelData ?? null))
  }, [dispatch, growthWheelData])

  useEffect(() => {
    dispatch(setUserCompetencyAreasData(userCompetencyAreasData ?? []))
    dispatch(evaluateFocusCompetenciesLimit())
    dispatch(setCurrentAreasOfFocus())
  }, [dispatch, userCompetencyAreasData])

  useEffect(() => {
    dispatch(setLoadingGrowthWheel(isLoading))
  }, [dispatch, isLoading])

  useEffect(() => {
    if (!userCompetencyAreasData?.length) return
    if (currentCompetencyArea) {
      const freshArea = userCompetencyAreasData.find(
        a => a.id === currentCompetencyArea.id,
      )
      if (freshArea) {
        dispatch(setCurrentCompetencyArea(freshArea))
        if (currentCompetency) {
          const freshCompetency = freshArea.competencies?.find(
            c => c.id === currentCompetency.id,
          )
          if (freshCompetency) dispatch(setCurrentCompetency(freshCompetency))
        }
      }
    }
  }, [
    userCompetencyAreasData,
    currentCompetencyArea,
    currentCompetency,
    dispatch,
  ])

  const handleSelectContributor = (contributorId: number) => {
    setSelectedUserId(contributorId)
    dispatch(setCurrentCompetencyArea(null))
    dispatch(setCurrentCompetency(null))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleBackToContributors = () => {
    setSelectedUserId(null)
    dispatch(setCurrentCompetencyArea(null))
    dispatch(setCurrentCompetency(null))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const displayAssessmentView = currentCompetency && currentCompetencyArea
  const displayAreaView = currentCompetencyArea && !currentCompetency
  const displayDashboardView = !currentCompetencyArea && growthWheelData

  return (
    <main>
      {!selectedUserId ? (
        <AdminContributorList
          contributors={contributorsData || []}
          onSelectContributor={handleSelectContributor}
        />
      ) : (
        <div className="container mb-5 gw-growth-wheel-container">
          <div className="my-5">
            <button
              onClick={handleBackToContributors}
              className="btn btn-link text-decoration-none text-secondary p-0"
            >
              ← Back to team members
            </button>
          </div>

          {displayAssessmentView && <GrowthWheelCompetencyAssessment isAdminView={true} />}
          {displayAreaView && <GrowthWheelCompetencyAreaView />}
          {displayDashboardView && <GrowthWheelDashboard userName={selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : ''} />}
        </div>
      )}
    </main>
  )
}

export default GrowthWheelAdmin
